import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { FuseCardComponent } from '@fuse/components/card';
import { AlertLabelPipe, BadgePipe, CountBadgeRingPipe } from 'app/core/pipes/badge.pipe';
import { SweetAlertService } from 'app/core/services/sweet-alert.service';
import { StatisticsService } from 'app/modules/statistics/statistics.service';
import { AuthService } from '../../../auth/auth.service';

@Component({
  selector: 'app-my-todos',
  standalone: true,
  imports: [NgClass, FuseCardComponent, MatIconModule, MatProgressSpinnerModule, MatTooltipModule, BadgePipe, AlertLabelPipe, CountBadgeRingPipe],
  templateUrl: './my-todos.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyTodosComponent implements OnInit, OnChanges {
  private statistics = inject(StatisticsService);
  private auth = inject(AuthService);
  private router = inject(Router);
  private sweetAlert = inject(SweetAlertService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  profile: any;
  loadingCount = 0;
  error: string | null = null;
  todos: any[] = [];
  totalCount = 0;

  private todoList: { [key: string]: { route: string; params?: any; tooltip?: string } } = {
    HAPPINESS_CENTER_UNREPLIED_MESSAGES: { route: '/happiness-center/online' },
    JOBS_UNREPLIED_MESSAGES: { route: '/procurement/communications/list' },
    BILLS_IN_INBOX: {
      route: '/accounting/bills',
      tooltip: 'Bills with status: Sent for Review, Sent for Approval, Sent Back, or Sent for Audit Approval',
    },
    CREDIT_NOTES_IN_INBOX: { route: '/accounting/credit-notes' },
    RFPS_IN_INBOX: { route: '/procurement/rfps/inbox' },
    WORK_ORDERS_IN_INBOX: { route: '/procurement/work-orders/inbox' },
    INCOMING_PAYMENTS: { route: '/accounting/online-payments', params: { tab: 'New', status: 'New' } },
    ADVANCE_PAYMENTS_IN_INBOX: { route: '/accounting/advance-payments' },
    UNREVIEWED_JOB_QUOTES: { route: '/procurement/rfps/active' },
    COMPANY_COMPLIANCE_EXPIRED: { route: '/compliances', params: { tab: 'vendor' } },
    COMMUNITY_COMPLIANCE_EXPIRED: { route: '/reports/administration/compliances-report' },
    INSURANCE_EXPIRED: { route: '/insurances/list' },
    INSURANCE_EXPIRING: { route: '/insurances/list' },
    VIOLATION_NOTICES: { route: '/violations/notices' },
    CONTRACT_RENEWAL_REMINDER: { route: '/procurement/contracts' },
    PDC_DUE: { route: '/accounting/pdcs' },
  };

  private permissionMap: { [key: string]: string } = {
    JOBS_UNREPLIED_MESSAGES: 'workorder_view',
    RFPS_IN_INBOX: 'rfp_view',
    WORK_ORDERS_IN_INBOX: 'workorder_view',
    UNREVIEWED_JOB_QUOTES: 'rfp_view',
    COMMUNITY_COMPLIANCE_EXPIRED: 'compliance_report_view',
    COMPANY_COMPLIANCE_EXPIRED: 'company_compliance_view',
  };

  @Input() fromDate: any = '';
  @Input() toDate: any = '';
  @Input() associationId: string | null = null;

  get loading(): boolean {
    return this.loadingCount > 0;
  }

  ngOnInit() {
    this.profile = this.auth.profile();
  }

  ngOnChanges(_changes: SimpleChanges) {
    this.getData();
  }

  navigate(todo: any): void {
    if (!todo.route) return;
    const perm = this.permissionMap[todo.key];
    if (perm && !this.auth.canAccess([perm])) {
      this.sweetAlert.info('You do not have permission to access this page.', 'Access Denied');
      return;
    }
    this.router.navigate([todo.route], { queryParams: todo.params });
  }

  private getData(): void {
    this.todos = [];
    this.loadingCount++;
    const params: any = { fromDate: this.fromDate, toDate: this.toDate };
    if (this.associationId) params.association_id = this.associationId;
    this.statistics
      .getMyTodos(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp: any) => {
          this.profile = this.auth.profile();
          const records: any[] = Object.values(resp.record);
          this.totalCount = records.length;
          const advancePayment = records.find((todo: any) => todo.key === 'ADVANCE_PAYMENTS_IN_INBOX');
          const others = records.filter((todo: any) => todo.key !== 'ADVANCE_PAYMENTS_IN_INBOX').slice(0, 5);
          const ordered = advancePayment ? [...others, advancePayment] : others;
          this.todos = ordered.map((todo: any) => ({
            key: todo.key,
            alert: todo.alert,
            message: todo.message,
            route: this.todoList[todo.key]?.route,
            params: { ...this.todoList[todo.key]?.params, _ref: 'dashboard', user_id: this.profile?.id },
            tooltip: this.todoList[todo.key]?.tooltip,
          }));
          this.totalCount = this.todos.length;
          this.todos = this.todos.slice(0, 5);
          this.loadingCount--;
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          this.error = err.message || 'An error occurred while getting the Todos data';
          this.loadingCount--;
          this.cdr.markForCheck();
        },
      });
  }
}
